import { registerPlugin, PluginListenerHandle } from "@capacitor/core";
import { FaceDetectionWeb } from "./web/faceDetectionWeb";

// ============================================================
// ⭐ FACE DISTANCE FACTOR — SINGLE SOURCE OF TRUTH ⭐
// ============================================================
// The ONE place to tune how close a person must be for the face
// scan to run. Cameras have no depth sensor, so distance is derived
// from how much of the camera frame the face fills:
//
//     ratio = face bounding-box height / video frame height
//
// The SMALLER the ratio, the FARTHER the person is from the device.
//
// Used by BOTH sides automatically:
//   • Web    → faceDetectionWeb.ts reads these as its defaults.
//   • Native → FaceClockInOut / FaceRegistrationModal pass these into
//              every startActiveScan() call, so the Android (MLKit)
//              and iOS (Vision) plugins receive the same values over
//              the Capacitor bridge.
// → Change the values here and Web + Android + iOS all update together.
//
// ── Value guide (typical front camera, ~55° vertical FOV) ──
//   0.15 → face fills 15% of frame height ≈ ~1.6 m (very lenient)
//   0.25 → face fills 25% of frame height ≈ ~1.0 m (arm's length) ← CURRENT
//   0.35 → ≈ ~0.7 m
//   0.45 → face fills 45% of frame height ≈ ~0.5 m (strict / close-up)
//
// ── Want the ORIGINAL flow (scan a face at ANY distance — the
//    behaviour before the distance factor existed)? ──
//   Set  MIN_FACE_SIZE_RATIO = 0  → the distance gate is disabled on
//   Web, Android and iOS (native had no gate before; Web's legacy
//   fallback was a very lenient 0.08).
//   Optionally also set  MAX_FACE_SIZE_RATIO = 1  to remove the
//   "too close" guard (0.98 only rejects a face pressed against the lens).
// ============================================================
export const FACE_DISTANCE_FACTOR = {
  /** Min face-height / frame-height ratio (0-1). 0 = distance gate OFF. */
  MIN_FACE_SIZE_RATIO: 0.15,
  /** Max face-height / frame-height ratio (0-1). 1 = no too-close guard. */
  MAX_FACE_SIZE_RATIO: 0.98,
} as const;

// ============================================================
// ⭐ MULTI-FACE POLICY — SINGLE SOURCE OF TRUTH ⭐
// ============================================================
// How multiple detected faces are judged. The MOST PROMINENT face
// (largest bounding box = closest to the camera) is always the scan
// candidate. Secondary faces are IGNORED as background when ANY of:
//   1. Their bounding-box area is below SECONDARY_FACE_AREA_RATIO of
//      the primary's area (area scales with distance², so 0.5 ≈ the
//      other person is ~1.4x farther away), OR
//   2. They are clipped by the frame edge (half-visible person walking
//      past / standing at the side of the shot), OR
//   3. Their centre lies inside the primary face's box (directly
//      behind the primary person's head).
// Only secondary faces of COMPARABLE prominence (fully visible, similar
// size, not behind the primary) trigger the "Multiple faces detected"
// error — because in that case the intended face is genuinely ambiguous.
// Used by Web, Android (MLKit) & iOS (Vision) via the faceScanned event
// and by processFaceImageFrontend for captured-image validation.
// ============================================================
export const FACE_MULTI_FACE_POLICY = {
  /** Secondary face area below this fraction of the primary's area = background. */
  SECONDARY_FACE_AREA_RATIO: 0.5,
  /** A face touching the frame edge within this fraction of the frame size = clipped/partial. */
  EDGE_CLIP_TOLERANCE: 0.02,
} as const;

/** Minimal face shape the multi-face helper works with. */
export interface FaceBoxLike {
  bounds: { left: number; top: number; right: number; bottom: number };
}

function faceBoxArea(face: FaceBoxLike): number {
  return (
    Math.max(0, face.bounds.right - face.bounds.left) *
    Math.max(0, face.bounds.bottom - face.bounds.top)
  );
}

function isFaceClippedByFrame(
  face: FaceBoxLike,
  frameWidth: number,
  frameHeight: number,
): boolean {
  if (!frameWidth || !frameHeight) return false; // dims unknown — skip this rule
  const tolX = frameWidth * FACE_MULTI_FACE_POLICY.EDGE_CLIP_TOLERANCE;
  const tolY = frameHeight * FACE_MULTI_FACE_POLICY.EDGE_CLIP_TOLERANCE;
  return (
    face.bounds.left <= tolX ||
    face.bounds.top <= tolY ||
    face.bounds.right >= frameWidth - tolX ||
    face.bounds.bottom >= frameHeight - tolY
  );
}

/**
 * Picks the most prominent (largest = closest) face and decides whether the
 * remaining faces are ignorable background or a genuine ambiguity.
 *
 * Returns `{ primary, ambiguous }`:
 *  - `primary`   — the largest face (null when the list is empty)
 *  - `ambiguous` — true ONLY when at least one secondary face is comparably
 *                  prominent (fully visible, similar size, not behind the
 *                  primary). Background / partial faces never count.
 */
export function selectPrimaryFace<T extends FaceBoxLike>(
  faces: T[],
  frameWidth: number,
  frameHeight: number,
): { primary: T | null; ambiguous: boolean } {
  if (!faces || faces.length === 0) {
    return { primary: null, ambiguous: false };
  }

  const sorted = [...faces].sort((a, b) => faceBoxArea(b) - faceBoxArea(a));
  const primary = sorted[0];
  if (sorted.length === 1) {
    return { primary, ambiguous: false };
  }

  const primaryArea = faceBoxArea(primary);
  const ambiguous = sorted.slice(1).some((secondary) => {
    // 1. Much smaller than the primary → far away in the background.
    if (faceBoxArea(secondary) < primaryArea * FACE_MULTI_FACE_POLICY.SECONDARY_FACE_AREA_RATIO) {
      return false;
    }
    // 2. Cut off by the frame edge → partial face (half head at the side).
    if (isFaceClippedByFrame(secondary, frameWidth, frameHeight)) {
      return false;
    }
    // 3. Centre inside the primary's box → directly behind the primary person.
    const cx = (secondary.bounds.left + secondary.bounds.right) / 2;
    const cy = (secondary.bounds.top + secondary.bounds.bottom) / 2;
    if (
      cx >= primary.bounds.left &&
      cx <= primary.bounds.right &&
      cy >= primary.bounds.top &&
      cy <= primary.bounds.bottom
    ) {
      return false;
    }
    return true;
  });

  return { primary, ambiguous };
}

export interface FaceDetectionPlugin {
  startActiveScan(options: StartActiveScanOptions): Promise<void>;
  stopScan(): Promise<void>;
  captureFrame(): Promise<{ image: string }>;
  setIdleMode(options: { enabled: boolean }): Promise<void>;
  setMotionDetection(options: { enabled: boolean }): Promise<void>;
  checkPermissions(): Promise<PermissionStatus>;
  requestPermissions(): Promise<PermissionStatus>;
  addListener(
    eventName: "faceScanned",
    listenerFunc: (event: ActiveFaceScanResult) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "scanError",
    listenerFunc: (event: ScanErrorEvent) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "motionDetected",
    listenerFunc: (event: { trigger: string }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  removeAllListeners(): Promise<void>;
}

export interface StartActiveScanOptions {
  lensFacing?: LensFacing;
  performanceMode?: PerformanceMode;
  landmarkMode?: LandmarkMode;
  contourMode?: ContourMode;
  classificationMode?: ClassificationMode;
  minFaceSize?: number;
  enableTracking?: boolean;
  /**
   * Distance factor (lower bound, 0-1): the detected face's bounding-box height
   * must be at least this fraction of the video frame height for the scan to
   * accept it. Faces below this ratio are rejected and reported with
   * `tooFar: true`. 0 disables the gate.
   *
   * ⭐ Tune this in ONE place: components pass
   * `FACE_DISTANCE_FACTOR.MIN_FACE_SIZE_RATIO` (see top of this file).
   */
  minFaceSizeRatio?: number;
  /**
   * Distance factor (upper bound, 0-1): reject faces occupying more than this
   * fraction of the frame height (pressed against the lens). 1 disables the
   * too-close guard.
   *
   * ⭐ Tune this in ONE place: components pass
   * `FACE_DISTANCE_FACTOR.MAX_FACE_SIZE_RATIO` (see top of this file).
   */
  maxFaceSizeRatio?: number;
}

export interface ActiveFaceScanResult {
  face: Face | null;
  /**
   * ALL faces that passed the distance gate, sorted most prominent
   * (largest/closest) first. Lets the UI apply the shared multi-face
   * policy (FACE_MULTI_FACE_POLICY / selectPrimaryFace).
   */
  faces?: Face[];
  facesCount?: number;
  /** Analysis frame dimensions (for multi-face prominence checks). */
  frameWidth?: number;
  frameHeight?: number;
  /**
   * True when at least one face was detected but every one of them was
   * rejected by the distance factor (all faces too far away — e.g. someone
   * just walking past the device). The UI can show "move closer" guidance.
   */
  tooFar?: boolean;
}

export interface ScanErrorEvent {
  message: string;
}

export interface Face {
  bounds: Rect;
  landmarks?: FaceLandmark[];
  contours?: FaceContour[];
  trackingId?: number;
  headEulerAngleX: number;
  headEulerAngleY: number;
  headEulerAngleZ: number;
  smilingProbability?: number;
  leftEyeOpenProbability?: number;
  rightEyeOpenProbability?: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface FaceLandmark {
  type: number;
  position: Point;
}

export interface FaceContour {
  type: number;
  points: Point[];
}

export interface Point {
  x: number;
  y: number;
}

export interface PermissionStatus {
  camera: "granted" | "denied" | "prompt" | "prompt-with-rationale";
}

export enum PerformanceMode {
  Fast = 1,
  Accurate = 2,
}

export enum LandmarkMode {
  None = 1,
  All = 2,
}

export enum ContourMode {
  None = 1,
  All = 2,
}

export enum ClassificationMode {
  None = 1,
  All = 2,
}

export enum LensFacing {
  Front = "FRONT",
  Back = "BACK",
}

export enum LandmarkType {
  MouthBottom = 0,
  LeftCheek = 1,
  LeftEar = 3,
  LeftEye = 4,
  MouthLeft = 5,
  NoseBase = 6,
  RightCheek = 7,
  RightEar = 9,
  RightEye = 10,
  MouthRight = 11,
}

export const FaceDetection = registerPlugin<FaceDetectionPlugin>(
  "FaceDetectionPlugin",
  {
    web: () => new FaceDetectionWeb(),
  },
);
